import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import Notification from '../../../components/Notification/Notification'
import Loading from '../../../components/Loading/Loading'
import useNotification from '../../../hooks/useNotification'

import LandskampMatchModal from './LandskampMatchModal'

import { LANDSKAMP_MATCHES } from '../../../graphql/queries'
import { DELETE_LANDSKAMP_MATCH, UPSERT_LANDSKAMP_MATCH } from '../../../graphql/mutations'

import './LandskampAdmin.css'
import { COMPETITIONS, DEFAULT_TEAMS, EMPTY_FORM, STADIUM_BY_HOME_TEAM, TEAMS_BY_COMPETITION } from './landskampAdmin.constants'
import useInitialMatchEdit from '../hooks/useInitialMatchEdit'

function toLocalDatetimeValue(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

function fromLocalDatetimeValue(value) {
  if (!value) return null
  // Browser giver lokal tid; vi gemmer som ISO
  const d = new Date(value)
  return d.toISOString()
}

const emptyForm = EMPTY_FORM

export default function LandskampAdmin({
  initialEditMatchId = null,
  onInitialEditHandled,
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [venueTouched, setVenueTouched] = useState(false)
  const [cityTouched, setCityTouched] = useState(false)
  const lastAutoVenueRef = useRef(null)
  const lastAutoCityRef = useRef(null)
  const { showNotification, notificationProps } = useNotification()

  const { data, loading, refetch } = useQuery(LANDSKAMP_MATCHES, {
    fetchPolicy: 'cache-and-network',
    onError: (e) => showNotification(e.message || 'Kunne ikke hente kampe.', 'error'),
  })
  const matches = useMemo(() => (Array.isArray(data?.landskampMatches) ? data.landskampMatches : []), [data])

  const competitionOptions = useMemo(() => {
    const list = [...COMPETITIONS]
    const current = (form.competition || '').trim()
    if (current && !list.includes(current)) list.unshift(current)
    return list
  }, [form.competition])

  const teamOptions = useMemo(() => {
    const key = (form.competition || '').trim()
    const base = TEAMS_BY_COMPETITION[key] || DEFAULT_TEAMS
    const list = Array.isArray(base) ? [...base] : [...DEFAULT_TEAMS]

    const ensure = (v) => {
      const val = (v || '').trim()
      if (!val) return
      if (!list.includes(val)) list.unshift(val)
    }

    ensure(form.homeTeam)
    ensure(form.awayTeam)

    // Fjern duplicates, og sorter alfabetisk (da-DK)
    const unique = Array.from(new Set(list))
    const pinned = []
    const pin = (v) => {
      const val = (v || '').trim()
      if (!val) return
      if (!pinned.includes(val)) pinned.push(val)
    }
    pin(form.homeTeam)
    pin(form.awayTeam)

    const rest = unique
      .filter((t) => !pinned.includes(t))
      .sort((a, b) => a.localeCompare(b, 'da-DK'))

    return [...pinned, ...rest]
  }, [form.competition, form.homeTeam, form.awayTeam])

  const venueOptions = useMemo(() => {
    const home = (form.homeTeam || '').trim()
    const defaultVenue = STADIUM_BY_HOME_TEAM[home]?.venue || ''
    const currentVenue = (form.venue || '').trim()

    const all = Object.values(STADIUM_BY_HOME_TEAM)
      .map((x) => x?.venue)
      .filter(Boolean)

    const unique = Array.from(new Set(all))
    const pinned = []
    const pin = (v) => {
      const val = (v || '').trim()
      if (!val) return
      if (!pinned.includes(val)) pinned.push(val)
    }
    pin(defaultVenue)
    pin(currentVenue)

    const rest = unique
      .filter((v) => !pinned.includes(v))
      .sort((a, b) => a.localeCompare(b, 'da-DK'))

    return [...pinned, ...rest]
  }, [form.homeTeam, form.venue])

  const cityOptions = useMemo(() => {
    const home = (form.homeTeam || '').trim()
    const defaultCity = STADIUM_BY_HOME_TEAM[home]?.city || ''
    const currentCity = (form.city || '').trim()

    const all = Object.values(STADIUM_BY_HOME_TEAM)
      .map((x) => x?.city)
      .filter(Boolean)

    const unique = Array.from(new Set(all))
    const pinned = []
    const pin = (v) => {
      const val = (v || '').trim()
      if (!val) return
      if (!pinned.includes(val)) pinned.push(val)
    }
    pin(defaultCity)
    pin(currentCity)

    const rest = unique
      .filter((v) => !pinned.includes(v))
      .sort((a, b) => a.localeCompare(b, 'da-DK'))

    return [...pinned, ...rest]
  }, [form.homeTeam, form.city])

  useEffect(() => {
    if (!modalOpen) return
    const home = (form.homeTeam || '').trim()
    const defaults = STADIUM_BY_HOME_TEAM[home]
    if (!defaults) return

    setForm((prev) => {
      const next = { ...prev }
      const venueVal = (prev.venue || '').trim()
      const cityVal = (prev.city || '').trim()

      if (!venueTouched && (!venueVal || venueVal === lastAutoVenueRef.current)) {
        next.venue = defaults.venue
        lastAutoVenueRef.current = defaults.venue
      }
      if (!cityTouched && (!cityVal || cityVal === lastAutoCityRef.current)) {
        next.city = defaults.city
        lastAutoCityRef.current = defaults.city
      }
      return next
    })
    // vi vil kun reagere på ændringer i hjemmehold + touched flags
  }, [modalOpen, form.homeTeam, venueTouched, cityTouched])

  const handleHomeTeamChange = (value) => {
    const nextHome = value
    const defaults = STADIUM_BY_HOME_TEAM[(nextHome || '').trim()]

    if (defaults) {
      // Når hjemmehold ændres til et kendt hold, så opdater Stadion/By til default
      setVenueTouched(false)
      setCityTouched(false)
      lastAutoVenueRef.current = defaults.venue
      lastAutoCityRef.current = defaults.city
      setForm((p) => ({
        ...p,
        homeTeam: nextHome,
        venue: defaults.venue,
        city: defaults.city,
      }))
      return
    }

    setForm((p) => ({ ...p, homeTeam: nextHome }))
  }

  const [upsertMatch, { loading: saving }] = useMutation(UPSERT_LANDSKAMP_MATCH, {
    onCompleted: () => {
      showNotification('Kamp gemt.', 'success')
      setModalOpen(false)
      setForm(emptyForm)
      refetch()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke gemme kamp.', 'error'),
  })

  const [deleteMatch, { loading: deleting }] = useMutation(DELETE_LANDSKAMP_MATCH, {
    onCompleted: () => {
      showNotification('Kamp slettet.', 'success')
      refetch()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke slette kamp.', 'error'),
  })

  const openNew = () => {
    setForm(emptyForm)
    setVenueTouched(false)
    setCityTouched(false)
    lastAutoVenueRef.current = null
    lastAutoCityRef.current = null
    setModalOpen(true)
  }

  const openEdit = useCallback((m) => {
    setForm({
      id: m.id,
      kickoffLocal: toLocalDatetimeValue(m.kickoff),
      homeTeam: m.homeTeam || '',
      awayTeam: m.awayTeam || '',
      competition: m.competition || '',
      venue: m.venue || '',
      city: m.city || '',
      homeScore: m.homeScore ?? '',
      awayScore: m.awayScore ?? '',
    })
    // Ved redigering skal vi ikke auto-overskrive eksisterende data
    setVenueTouched(true)
    setCityTouched(true)
    lastAutoVenueRef.current = null
    lastAutoCityRef.current = null
    setModalOpen(true)
  }, [])

  useInitialMatchEdit({
    initialEditMatchId,
    matches,
    loading,
    onOpenEdit: openEdit,
    onInitialEditHandled,
  })

  const submit = async () => {
    const kickoff = fromLocalDatetimeValue(form.kickoffLocal)
    if (!kickoff) {
      showNotification('Kickoff er påkrævet.', 'error')
      return
    }
    if (!form.homeTeam.trim() || !form.awayTeam.trim()) {
      showNotification('Hjemmehold og udehold er påkrævet.', 'error')
      return
    }

    const toIntOrNull = (v) => {
      if (v === '' || v === null || v === undefined) return null
      const n = Number(v)
      return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : null
    }

    await upsertMatch({
      variables: {
        input: {
          ...(form.id ? { id: form.id } : {}),
          kickoff,
          homeTeam: form.homeTeam.trim(),
          awayTeam: form.awayTeam.trim(),
          competition: form.competition.trim() || null,
          venue: form.venue.trim() || null,
          city: form.city.trim() || null,
          homeScore: toIntOrNull(form.homeScore),
          awayScore: toIntOrNull(form.awayScore),
        },
      },
    })
  }

  return (
    <div className="landskamp-admin">
      <div className="admin-page-toolbar">
        <Button title="Tilføj kamp" onClick={openNew} color="confirmation" />
      </div>

      <Notification {...notificationProps} />

      {loading ? (
        <div style={{ paddingTop: 20 }}>
          <Loading />
        </div>
      ) : (
        <div className="landskamp-admin-list">
          {matches.length === 0 ? (
            <Card hover={false}>
              <div>Ingen kampe endnu. Tryk “Ny kamp”.</div>
            </Card>
          ) : (
            matches.map((m) => (
              <Card key={m.id} className="landskamp-admin-item" hover={false}>
                <div className="landskamp-admin-item__main">
                  <div className="landskamp-admin-item__title">
                    <strong>
                      {m.homeTeam} vs {m.awayTeam}
                    </strong>
                  </div>
                  <div className="landskamp-admin-item__meta">
                    {new Date(m.kickoff).toLocaleString('da-DK')} {m.competition ? `· ${m.competition}` : ''}
                  </div>
                  <div className="landskamp-admin-item__meta">
                    {m.venue ? m.venue : ''}{m.city ? `, ${m.city}` : ''}
                  </div>
                  <div className="landskamp-admin-item__score">
                    Resultat: <strong>{m.homeScore ?? '-'}-{m.awayScore ?? '-'}</strong>
                  </div>
                </div>

                <div className="landskamp-admin-item__actions">
                  <Button title="Redigér" onClick={() => openEdit(m)} />
                  <Button
                    title="Slet"
                    variant="outlined"
                    color="danger"
                    disabled={deleting}
                    onClick={() => deleteMatch({ variables: { id: m.id } })}
                  />
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      <LandskampMatchModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        form={form}
        setForm={setForm}
        competitionOptions={competitionOptions}
        teamOptions={teamOptions}
        venueOptions={venueOptions}
        cityOptions={cityOptions}
        onHomeTeamChange={handleHomeTeamChange}
        onVenueChange={(v) => {
          setVenueTouched(true)
          setForm((p) => ({ ...p, venue: v }))
        }}
        onCityChange={(v) => {
          setCityTouched(true)
          setForm((p) => ({ ...p, city: v }))
        }}
        onSubmit={submit}
        saving={saving}
      />
    </div>
  )
}

